import * as THREE from 'three';
import SvgRepresentation2d from '../representation2d/SvgRepresentation2d';
import { SVGLoader } from 'three/examples/jsm/loaders/SVGLoader.js';
import { sceneUnitsFromMm } from '../../utils/scaler';
import { defaultMaterial } from '../../utils/defaultMaterial';
import { LineSegments2 } from 'three/addons/lines/LineSegments2.js';
import { LineGeometry } from 'three/addons/lines/LineGeometry.js';
import { LineMaterial } from 'three/addons/lines/LineMaterial.js';
import { convertStringValueToMm } from '../../utils/unitConversions';
import { findPathsById, parseInsertionPointFromPath } from '../../utils/svgInsertionPointParser';

export default class SvgAsyncGeometry {
	group: THREE.Group;
	widthSU: number | null = null;
	heightSU: number | null = null;
	#isDisposed: boolean = false;

	private static fillMaterialCache = new Map<string, THREE.MeshBasicMaterial>();
	private static strokeMaterialCache = new Map<string, LineMaterial>();

	private static getFillMaterial(color: THREE.Color): THREE.MeshBasicMaterial {
		const key = color.getHexString();
		let mat = SvgAsyncGeometry.fillMaterialCache.get(key);
		if (!mat) {
			mat = new THREE.MeshBasicMaterial({ color, side: THREE.DoubleSide, name: `SVGAsync-Fill-#${key}` });
			SvgAsyncGeometry.fillMaterialCache.set(key, mat);
		}
		return mat;
	}

	private static getStrokeMaterial(
		strokeColor: string,
		dashed: boolean,
		dashScale: number,
		resolution: THREE.Vector2
	): LineMaterial {
		const dashScaleKey = dashScale.toFixed(4);
		const key = `${strokeColor}|${dashed}|${dashScaleKey}`;
		let mat = SvgAsyncGeometry.strokeMaterialCache.get(key);
		if (!mat) {
			const colorHex = new THREE.Color(strokeColor).getHexString();
			mat = new LineMaterial({
				color: new THREE.Color(strokeColor) as any,
				linewidth: 1,
				resolution,
				transparent: true,
				opacity: 0.5,
				dashed,
				dashSize: sceneUnitsFromMm(50),
				gapSize: sceneUnitsFromMm(50),
				dashScale,
				name: `SVGAsync-Stroke-#${colorHex}${dashed ? '-dashed' : ''}`
			});
			SvgAsyncGeometry.strokeMaterialCache.set(key, mat);
		}
		return mat;
	}

    constructor(	svgRepresentation: SvgRepresentation2d, 
					svgUrl: string,
					layer: number, 
					position: THREE.Vector3, 
					width: number | null, 
					height: number | null) {
		this.group = new THREE.Group();

		this.widthSU = width ? sceneUnitsFromMm(width) : null;
		this.heightSU = height ? sceneUnitsFromMm(height) : null;

		const w = this.widthSU ?? sceneUnitsFromMm(100);
		const h = this.heightSU ?? sceneUnitsFromMm(100);
		const d = sceneUnitsFromMm(100);

		const geometry = new THREE.BoxGeometry(w, h, d);
		
		const cube = new THREE.Mesh(geometry, defaultMaterial);
		cube.position.set(w / 2, h / 2, d / 2);
		cube.layers.set(layer);

		this.group.add(cube);
		this.group.position.copy(position);
		this.group.layers.set(layer);

		this.loadSvg(svgUrl);
	}

	async loadSvg(svgUrl: string) {
		const loader = new SVGLoader();

		const svg = await loader.loadAsync(svgUrl);

		// Guard: the geometry may have been disposed while waiting for the network request.
		if (this.#isDisposed) return;

		this.group.clear();

		// Parse sizes and insertion point, compute transforms, and build meshes/lines

		

		let paths = svg.paths.slice();

		const element = svg.xml as unknown as SVGSVGElement;
		const svgWidthMm = convertStringValueToMm(element.getAttribute('width'));
		const svgHeightMm = convertStringValueToMm(element.getAttribute('height'));

		// Find insertion point
		const insertionPointPaths = findPathsById(paths, 'insertion-pt');
		let insertionPtSU = new THREE.Vector2(0, 0);
		let insertionPtPath: (typeof paths)[number] | null = null;

		if (insertionPointPaths.length > 0) {
			const parsed = parseInsertionPointFromPath(insertionPointPaths[0]);
			if (parsed) {
				insertionPtSU = new THREE.Vector2(parsed.x, parsed.y);
				insertionPtPath = insertionPointPaths[0];
			}
		}

		if (insertionPtPath) {
			paths = paths.filter(p => p !== insertionPtPath);
		}

		const isPointFixture = insertionPtSU.length() > 0;

		// Derive desired width/height from the placeholder cube (created in constructor)
		const placeholderMesh = this.group.children.find(
			c => (c as any).isMesh && (c as THREE.Mesh).geometry && ((c as THREE.Mesh).geometry as any).parameters
		) as THREE.Mesh | undefined;

		// Compute actual size and scale
		const actualWidthSU = !isPointFixture && this.widthSU ? this.widthSU : (svgWidthMm ? sceneUnitsFromMm(svgWidthMm) : 1.0);
		const actualHeightSU = !isPointFixture && this.heightSU ? this.heightSU : (svgHeightMm ? sceneUnitsFromMm(svgHeightMm) : 1.0);

		const scale = svgWidthMm && svgHeightMm
			? new THREE.Vector3(actualWidthSU / svgWidthMm, actualHeightSU / svgHeightMm, 1)
			: new THREE.Vector3(1, 1, 1);

		// Flip around X-axis
		const q = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), Math.PI);

		// Compute offsets
		const svgHeightBeforeScalingSU = svgHeightMm ?? 0;
		const yOffset = svgHeightBeforeScalingSU * scale.y;
		const insertionPtYOffset = isPointFixture ? insertionPtSU.y - yOffset : 0.0;

		// Apply transforms to the group
		this.group.quaternion.copy(q);
		this.group.scale.copy(scale);
		this.group.position.x += isPointFixture ? -insertionPtSU.x : 0.0;
		this.group.position.y += yOffset + insertionPtYOffset;

		// Remove placeholder cube
		if (placeholderMesh) {
			this.group.remove(placeholderMesh);
			placeholderMesh.geometry.dispose();
		}

		// Build fills
		const layerMask = this.group.layers.mask;
		const fillPaths = paths.filter(p => p.userData?.style?.fill !== 'none');

		for (const path of fillPaths) {
			const shapes = path.toShapes(false);
			if (!shapes || shapes.length === 0) continue;

			const geom = new THREE.ShapeGeometry(shapes as any);
			const mat = SvgAsyncGeometry.getFillMaterial(path.color);
			const mesh = new THREE.Mesh(geom, mat);
			mesh.layers.mask = layerMask;
			this.group.add(mesh);
		}

		// Build strokes
		const resolution = new THREE.Vector2(
			typeof window !== 'undefined' ? window.innerWidth : 1920,
			typeof window !== 'undefined' ? window.innerHeight : 1080
		);

		for (let i = 0; i < paths.length; i++) {
			const path = paths[i];
			for (let j = 0; j < path.subPaths.length; j++) {
				const subPath = path.subPaths[j];
				const points = subPath.getPoints();
				if (!points || points.length < 2) continue;

				const positions: number[] = [];
				for (let k = 0; k < points.length; k++) {
					positions.push(points[k].x, points[k].y, 0);
				}

				const geometry = new LineGeometry();
				geometry.setPositions(positions);

				const strokeWidth = 1;
				const strokeColor =
					(path.userData?.style && path.userData.style['stroke']) ? path.userData.style['stroke'] : 'green';

				const strokeDasharray = path.userData?.node?.style?.strokeDasharray;
				const dashed = !!(strokeDasharray && strokeDasharray !== 'none');

				const material = SvgAsyncGeometry.getStrokeMaterial(
					strokeColor,
					dashed,
					(scale.x + scale.y) / 2,
					resolution
				);

				const lineSegs = new LineSegments2(geometry, material);
				lineSegs.layers.mask = layerMask;
				lineSegs.computeLineDistances();
				this.group.add(lineSegs);
			}
		}

	}

	dispose(): void {
		this.#isDisposed = true;
		this.group.traverse((child) => {
			if (child instanceof THREE.Mesh) {
				child.geometry.dispose();
			}
		});
		if (this.group.parent) {
			this.group.parent.remove(this.group);
		}
		this.group.clear();
	}
}
